import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import CampoFirma from "@/components/CampoFirma";
import { Html5Qrcode } from "html5-qrcode";
import { Search, QrCode, ArrowLeft, ArrowRight, Trash2, CheckCircle2, X, Camera, Plus, Minus, RotateCcw, History } from "lucide-react";
import ItemQRScanner from "@/components/ItemQRScanner";
import { DEFAULT_ITEM_PHOTO, DEFAULT_EMPLOYEE_PHOTO, photoUrl } from "@/lib/placeholders";

const ITEM_CATEGORIES = ["Tutti", "Protezione piedi", "Alta visibilità", "Saldatura", "Mani", "Cadute", "Capo", "Non DPI"];

export default function Consegna() {
  const [step, setStep] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [emp, setEmp] = useState(null);
  const [lines, setLines] = useState([]); // {item_id, name, size, quantity, max}
  const [notes, setNotes] = useState("");
  const [empFilter, setEmpFilter] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [showItemScanner, setShowItemScanner] = useState(false);
  const [firma, setFirma] = useState(null);
  // La consegna appena registrata: resta scritta in cima, perche' dopo aver
  // consegnato si consegna ancora. Chi distribuisce i DPI fa dieci consegne
  // di fila, e finire ogni volta sullo storico vuol dire dieci volte
  // «Nuova consegna» per tornare dove si era.
  const [ultima, setUltima] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/employees").then(({data}) => setEmployees(data));
    api.get("/items").then(({data}) => setItems(data));
  }, []);

  const filteredEmps = employees.filter(e => e.full_name.toLowerCase().includes(empFilter.toLowerCase()) || (e.badge_number || "").toLowerCase().includes(empFilter.toLowerCase()));

  // Dal QR si arriva qui con il dipendente (o l'articolo) gia' scelto.
  const [articoloIniziale, setArticoloIniziale] = useState(null);
  useEffect(() => {
    let p;
    try { p = new URLSearchParams(window.location.search); } catch { return; }
    const dip = p.get("dipendente");
    if (dip && employees.length && !emp) {
      const found = employees.find(e => e.id === dip);
      if (found) { setEmp(found); setStep(2); }
    }
    const art = p.get("articolo");
    if (art && items.length && !articoloIniziale) {
      const it = items.find(i => i.id === art);
      if (it) { setArticoloIniziale(it); if (emp) setStep(2); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, items]);

  const addLine = (item, size) => {
    const max = item.stock?.[size] || 0;
    if (max <= 0) { toast.error("Scorta esaurita"); return; }
    setLines([...lines, { item_id: item.id, name: item.name, size, quantity: 1, max }]);
  };
  const updateQty = (idx, delta) => {
    const arr = [...lines];
    arr[idx].quantity = Math.max(1, Math.min(arr[idx].max, arr[idx].quantity + delta));
    setLines(arr);
  };
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!firma) {
      toast.error("Firma del dipendente mancante");
      return;
    }
    const signature = firma;
    const chi = emp;
    const capi = lines.reduce((somma, l) => somma + l.quantity, 0);
    try {
      const { data } = await api.post("/deliveries", {
        employee_id: emp.id,
        lines: lines.map(l => ({ item_id: l.item_id, size: l.size, quantity: l.quantity })),
        signature_base64: signature, notes,
      });
      toast.success("Consegna registrata · " + data.delivery.id);
      setUltima({ id: data.delivery.id, chi, capi, articoli: lines.length });
      // Si riparte da zero, e le scorte si rileggono: le taglie appena
      // consegnate sono scese, e la consegna dopo deve vedere il magazzino
      // vero, non quello di cinque minuti fa.
      setEmp(null); setLines([]); setNotes(""); setFirma(null);
      setEmpFilter(""); setArticoloIniziale(null); setStep(1);
      api.get("/items").then(({ data: d }) => setItems(d)).catch(() => {});
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  /** Un'altra consegna alla stessa persona: capita, e sono due tocchi in meno. */
  const ancora = (chi) => {
    setUltima(null); setEmp(chi); setLines([]); setNotes(""); setFirma(null);
    setStep(2);
  };

  const onScannedCb = useCallback((empId) => {
    const found = employees.find(e => e.id === empId);
    if (found) { setEmp(found); setStep(2); setShowScanner(false); toast.success("Dipendente identificato"); }
    else toast.error("Dipendente non trovato");
  }, [employees]);
  const onCloseScanner = useCallback(() => setShowScanner(false), []);

  const onItemScanned = useCallback((itemId, size) => {
    setShowItemScanner(false);
    const item = items.find(i => i.id === itemId);
    if (!item) { toast.error("Articolo non trovato"); return; }
    if (!size) { toast.error("QR senza taglia — usa la ricerca manuale per scegliere la taglia"); return; }
    if (!item.sizes.includes(size)) { toast.error(`Taglia "${size}" non prevista per questo articolo`); return; }
    addLine(item, size);
  }, [items]);
  const onCloseItemScanner = useCallback(() => setShowItemScanner(false), []);

  return (
    <div className="space-y-6" data-testid="consegna-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">DPI</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Nuova consegna</h1>
      </div>

      {ultima && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="consegna-fatta">
          <CardContent className="p-4 flex items-start gap-3 flex-wrap">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-emerald-900">
                Consegnato a {ultima.chi.full_name}
              </div>
              <div className="text-xs text-emerald-800">
                {ultima.articoli === 1 ? "1 articolo" : `${ultima.articoli} articoli`}
                {" · "}{ultima.capi === 1 ? "1 capo" : `${ultima.capi} capi`}
                {" · "}firmato · modulo {ultima.id}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => ancora(ultima.chi)}
                      data-testid="ancora-a-lui">
                <RotateCcw className="w-3 h-3 mr-1" /> Un&apos;altra a {ultima.chi.full_name}
              </Button>
              <Button variant="outline" size="sm" onClick={() => nav("/storico")}>
                <History className="w-3 h-3 mr-1" /> Storico
              </Button>
              <Button variant="outline" size="sm" onClick={() => setUltima(null)} title="Chiudi">
                <X className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Steps indicator */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {[
          { n: 1, label: "Dipendente" },
          { n: 2, label: "Articoli" },
          { n: 3, label: "Firma" },
        ].map(s => (
          <div key={s.n} className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm ${step === s.n ? "bg-orange-500 text-white" : step > s.n ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            <span className="font-bold">{step > s.n ? "✓" : s.n}</span> {s.label}
          </div>
        ))}
      </div>

      {step === 1 && (
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-4">
            <div className="flex gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Cerca dipendente o badge..." className="pl-9 h-12" value={empFilter}
                       onChange={(e)=>setEmpFilter(e.target.value)} data-testid="emp-search" />
              </div>
              <Button variant="outline" className="h-12" onClick={() => setShowScanner(true)} data-testid="qr-scan-btn">
                <QrCode className="w-4 h-4 mr-2" /> Scansiona QR
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[500px] overflow-y-auto">
              {filteredEmps.map(e => (
                <button key={e.id} type="button" onClick={() => { setEmp(e); setStep(2); }}
                        className="text-left p-3 border border-slate-200 rounded hover:border-orange-500 hover:bg-orange-50 transition flex items-center gap-3"
                        data-testid={`emp-select-${e.id}`}>
                  <img src={photoUrl(e.photo_url) || DEFAULT_EMPLOYEE_PHOTO} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500 font-mono">{e.badge_number}</div>
                    <div className="font-semibold truncate">{e.full_name}</div>
                    <div className="text-xs text-slate-500 truncate">{e.role}</div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && emp && (
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500">DIPENDENTE</div>
                <div className="font-bold">{emp.full_name}</div>
                <div className="text-xs text-slate-500">{emp.badge_number} · {emp.role}</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setEmp(null); setStep(1); setLines([]); }}>Cambia</Button>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="font-display text-xl font-bold">Aggiungi articoli</h3>
                <Button variant="outline" onClick={() => setShowItemScanner(true)} data-testid="item-qr-scan-btn">
                  <QrCode className="w-4 h-4 mr-2" /> Scansiona QR articolo
                </Button>
              </div>
              <ItemPicker items={items} onAdd={addLine} iniziale={articoloIniziale} />
              {lines.length > 0 && (
                <div className="space-y-2 mt-4">
                  <h4 className="font-semibold text-sm">Articoli da consegnare ({lines.length})</h4>
                  {lines.map((l, i) => (
                    <div key={`${l.item_id}-${l.size}-${i}`} className="flex items-center gap-2 p-3 border border-slate-200 rounded">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{l.name}</div>
                        <div className="text-xs text-slate-500">Taglia {l.size} · disp. {l.max}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" onClick={() => updateQty(i, -1)}><Minus className="w-3 h-3" /></Button>
                        <span className="font-bold w-8 text-center">{l.quantity}</span>
                        <Button variant="outline" size="sm" onClick={() => updateQty(i, 1)}><Plus className="w-3 h-3" /></Button>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => removeLine(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(1)} className="h-12"><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
                <Button disabled={lines.length === 0} onClick={() => setStep(3)}
                        className="h-12 bg-orange-500 hover:bg-orange-600 text-white" data-testid="next-to-sign">
                  Prosegui alla firma <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-4">
              <div>
                <div className="text-xs text-slate-500 uppercase">Riepilogo</div>
                <h3 className="font-display text-xl font-bold">{emp.full_name}</h3>
              </div>
              <div className="border border-slate-200 rounded divide-y divide-slate-200">
                {lines.map((l, i) => (
                  <div key={`${l.item_id}-${l.size}-${i}`} className="p-3 flex items-center justify-between">
                    <span>{l.name} <Badge variant="outline">{l.size}</Badge></span>
                    <span className="font-bold">x{l.quantity}</span>
                  </div>
                ))}
              </div>
              <div>
                <Label>Note (opzionale)</Label>
                <Input value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Es: sostituzione per usura" />
              </div>
              <CampoFirma valore={firma} onChange={setFirma} obbligatoria
                          etichetta="Firma del dipendente"
                          descrizione="Si apre a tutto schermo: firma con il dito o con lo stilo." />
              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(2)} className="h-12"><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
                <Button onClick={submit} className="h-12 bg-orange-500 hover:bg-orange-600 text-white" data-testid="confirm-delivery">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Conferma consegna
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showScanner && <QRScanner onClose={onCloseScanner} onScanned={onScannedCb} />}
      {showItemScanner && <ItemQRScanner onClose={onCloseItemScanner} onScanned={onItemScanned} />}
    </div>
  );
}

function ItemPicker({ items, onAdd, iniziale = null }) {
  const [search, setSearch] = useState(iniziale ? iniziale.name : "");
  const [cat, setCat] = useState("Tutti");
  const [selected, setSelected] = useState(iniziale);
  const filtered = items.filter(i =>
    (cat === "Tutti" || i.category === cat) &&
    (i.name.toLowerCase().includes(search.toLowerCase()) || i.code.toLowerCase().includes(search.toLowerCase()))
  );
  return (
    <div className="space-y-3">
      <div className="flex gap-1 overflow-x-auto pb-1">
        {ITEM_CATEGORIES.map(c => (
          <button key={c} type="button" onClick={() => setCat(c)}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition ${
              cat === c ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}>{c}</button>
        ))}
      </div>
      <Input placeholder="Cerca articolo..." value={search} onChange={(e)=>setSearch(e.target.value)} className="h-11" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
        {filtered.map(it => (
          <button key={it.id} type="button" onClick={() => setSelected(it)}
                  className={`text-left p-3 border rounded flex gap-3 items-center ${selected?.id === it.id ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
            <img src={photoUrl(it.photo_url) || DEFAULT_ITEM_PHOTO} alt={it.name} className="w-12 h-12 rounded object-cover border border-slate-200 flex-shrink-0 bg-white" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-500 font-mono">{it.code} · {it.category}</div>
              <div className="font-semibold text-sm leading-tight">{it.name}</div>
            </div>
          </button>
        ))}
      </div>
      {selected && (
        <div className="p-3 border border-orange-200 bg-orange-50 rounded">
          <div className="font-semibold mb-2 text-sm">{selected.name} → seleziona taglia:</div>
          <div className="flex flex-wrap gap-2">
            {selected.sizes.map(s => {
              const q = selected.stock?.[s] || 0;
              return <Button key={s} type="button" disabled={q <= 0} variant="outline"
                onClick={() => { onAdd(selected, s); setSelected(null); setSearch(""); }}
                className={q <= 0 ? "opacity-50" : ""}>
                {s} <span className="ml-2 text-xs text-slate-500">({q})</span>
              </Button>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function QRScanner({ onClose, onScanned }) {
  useEffect(() => {
    let scanner;
    const start = async () => {
      try {
        scanner = new Html5Qrcode("qr-reader-box");
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
          (decoded) => {
            // QR encodes URL like /fascicolo/EMP001
            const m = decoded.match(/fascicolo\/([A-Z0-9_-]+)/i);
            if (m) {
              scanner.stop().then(() => onScanned(m[1]));
            } else if (decoded.startsWith("EMP")) {
              scanner.stop().then(() => onScanned(decoded));
            }
          }, () => {});
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.warn("QR scanner error:", e);
        toast.error("Camera non disponibile. Usa la ricerca manuale.");
        onClose();
      }
    };
    start();
    return () => { if (scanner?.isScanning) scanner.stop().catch(()=>{}); };
  }, [onClose, onScanned]);
  return (
    <div className="fixed inset-0 bg-slate-900/90 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-4 max-w-md w-full">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold flex items-center gap-2"><Camera className="w-4 h-4" /> Scansiona QR Dipendente</h3>
          <Button variant="outline" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div id="qr-reader-box" className="w-full" />
        <p className="text-xs text-slate-500 mt-3 text-center">Inquadra il QR sul badge del dipendente.</p>
      </div>
    </div>
  );
}
